import { useState } from 'react';
import { z } from 'zod';

/**
 * Custom hook for form validation using Zod schemas
 * @param {z.ZodSchema} schema - Zod schema to validate against
 * @returns {Object} Validation utilities
 */
export const useFormValidation = (schema) => {
    const [errors, setErrors] = useState({});

    /**
     * Validate entire form data
     * @param {Object} data - Form data to validate
     * @returns {Object} { success: boolean, data?: any, errors?: Object }
     */
    const validate = (data) => {
        try {
            const validatedData = schema.parse(data);
            setErrors({});
            return { success: true, data: validatedData };
        } catch (error) {
            const isZodError = error instanceof z.ZodError || error.name === 'ZodError' || (error.errors && Array.isArray(error.errors)) || (error.issues && Array.isArray(error.issues));

            if (isZodError) {
                const formattedErrors = {};
                const issues = error.errors || error.issues || [];
                issues.forEach((err) => {
                    const path = err.path.join('.');
                    formattedErrors[path] = err.message;
                });
                setErrors(formattedErrors);
                return { success: false, errors: formattedErrors };
            }
            const fallbackError = { _form: 'Validation failed' };
            setErrors(fallbackError);
            return { success: false, errors: fallbackError };
        }
    };

    /**
     * Validate a single field
     * @param {string} fieldName - Name of the field to validate
     * @param {any} value - Value to validate
     * @returns {boolean} True if valid, false otherwise
     */
    const validateField = (fieldName, value) => {
        try {
            const fieldSchema = schema.shape[fieldName];
            if (fieldSchema) {
                fieldSchema.parse(value);
                setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                });
                return true;
            }
        } catch (error) {
            const isZodError = error instanceof z.ZodError || error.name === 'ZodError' || (error.errors && Array.isArray(error.errors)) || (error.issues && Array.isArray(error.issues));

            if (isZodError) {
                const issues = error.errors || error.issues || [];
                const message = issues[0]?.message || 'Invalid value';
                setErrors((prev) => ({
                    ...prev,
                    [fieldName]: message
                }));
                return false;
            }
        }
        return true;
    };

    /**
     * Clear error for a specific field
     * @param {string} fieldName - Name of the field
     */
    const clearError = (fieldName) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    };

    /**
     * Clear all errors
     */
    const clearAllErrors = () => {
        setErrors({});
    };

    return {
        errors,
        validate,
        validateField,
        clearError,
        clearAllErrors
    };
};
